<template>
  <div
    class="fixed inset-x-0 bottom-0 z-header border-t border-light-bd-muted bg-light-bg-default px-4 pb-[calc(0.75rem+env(safe-area-inset-bottom))] pt-3 font-Spectral md:hidden"
    data-testid="price-bar"
  >
    <div class="flex items-baseline justify-between gap-x-4">
      <p class="text-sm text-light-fg-default">
        Total ({{ selectedCurrency }})
        <button aria-haspopup="dialog" class="ml-2 text-sm text-light-fg-accent underline underline-offset-2" type="button" @click="openDetails">Details</button>
      </p>
      <p class="whitespace-nowrap text-xl text-light-fg-default" data-testid="price-bar-total">{{ totalDisplay }}</p>
    </div>
  </div>
  <SlideUpModal aria-labelledby="price-details-title" close-data-testid="price-details-close" data-testid="price-details" :is-open="isDetailsOpen" :on-close="closeDetails">
    <h2 id="price-details-title" class="mb-4 pr-8 text-xl text-light-fg-default">Price details</h2>
    <TotalPriceSectionWithGuestType :applied-code="appliedCode" :experience-event="experienceEvent" :pre-reservation-price-with-guest-type="preReservationPriceWithGuestType">
      <template v-if="codeRedemption" #beforeTotal>
        <ReservationCodeDisclosure :redemption="codeRedemption" />
      </template>
    </TotalPriceSectionWithGuestType>
  </SlideUpModal>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';

import SlideUpModal from '@/wabunka-src/common/components/stateful/SlideUpModal/SlideUpModal.vue';
import { useCurrency } from '@/wabunka-src/common/composables/general/useCurrency/useCurrency';
import TotalPriceSectionWithGuestType from '@/wabunka-src/common/features/Experience/components/TotalPriceSection/TotalPriceSectionWithGuestType.vue';
import type { ReservationCodeRedemption } from '@/wabunka-src/common/features/Reservation/composables/forms/useReservationCodeRedemption';
import type { WabunkaExperienceEventViewModel } from '@/wabunka-src/features/Experience/models/experienceEvent';
import type { WabunkaCode } from '@/wabunka-src/features/Experience/subFeatures/Reservation/models/wabunkaCode';
import {
  formatTotalPriceDisplay,
  type ExperiencePreReservationPriceWithGuestTypeViewModel,
} from '@/wabunka-src/features/Experience/subFeatures/Reservation/models/wabunkaExperiencePreReservationPrice';
import ReservationCodeDisclosure from '@/wabunka-src/features/Experience/subFeatures/Reservation/pages/Reserve/components/ReservationSection/ReservationCodeDisclosure/ReservationCodeDisclosure.vue';

interface Props {
  experienceEvent: WabunkaExperienceEventViewModel;
  preReservationPriceWithGuestType?: ExperiencePreReservationPriceWithGuestTypeViewModel;
  appliedCode?: WabunkaCode;
  /** 内訳からも特典コードを入れられるようにする（D-031） */
  codeRedemption?: ReservationCodeRedemption;
}

const props = defineProps<Props>();

const { selectedCurrency } = useCurrency();

const totalDisplay = computed(() => {
  const price = props.preReservationPriceWithGuestType;
  // 見積もりが届くまで ¥0 と出さない。formatTotalPriceDisplay は既存の内訳（旧動線・PC 右カラム）と共有なので変えない
  if (!price) return '...';
  return formatTotalPriceDisplay(price, selectedCurrency.value);
});

// 固定バーの高さぶん、ページの末尾がバーの下に隠れないようにする
useHead({ bodyAttrs: { class: 'max-md:pb-20' } });

const isDetailsOpen = ref(false);
const openDetails = () => (isDetailsOpen.value = true);
const closeDetails = () => (isDetailsOpen.value = false);
</script>
